import { RefObject } from 'preact';

import useIsFullscreen from './useIsFullscreen';

/**
 * Viewport pixel threshold for detecting TV-connected scenarios.
 * Full HD (1920px) is the minimum TV resolution we target.
 * Using 1600 as threshold to account for browser chrome and safe margins.
 */
const TV_VIEWPORT_THRESHOLD = 1600;

/**
 * Regex to detect mobile user agents.
 * Matches devices using desktop modes (Android, Samsung DeX, Huawei Desktop, Motorola Ready For, etc.)
 * which report mobile UA but output to TV-sized displays.
 */
const MOBILE_UA_REGEX = /Android|iPhone|iPad|iPod|Mobi/i;

/**
 * Regex to detect Smart TV and console browser user agents.
 * These browsers run directly on TVs and should always scale in fullscreen.
 */
const TV_UA_REGEX =
  /SmartTV|SMART-TV|GoogleTV|Tizen|WebOS|webOS|NetCast|BRAVIA|Viera|Roku|CrKey|AppleTV|FireTV|Silk|PlayStation|Xbox|Nintendo/i;

interface FullscreenScaleResult {
  isFullscreen: boolean;
  shouldScale: boolean;
  scaleClassName: string | undefined;
}

/**
 * Hook to determine if fullscreen controls should be scaled up for TV displays.
 *
 * ## Supported Scenarios
 * 1. **Mobile desktop modes** (Samsung DeX, Huawei Desktop, Motorola Ready For):
 *    Detected via mobile UA + TV-sized viewport (≥1600px)
 * 2. **Smart TV browsers** (Samsung Tizen, LG webOS, Fire TV Silk, Android TV):
 *    Detected via TV-specific strings in user agent
 * 3. **Console browsers** (PlayStation, Xbox, Nintendo):
 *    Detected via console-specific strings in user agent
 *
 * ## Known Limitations & Trade-offs
 * - **Desktop/laptop connected via HDMI to TV will NOT scale**: We intentionally
 *   removed physical pixel detection (e.g., 4K resolution check) because it caused
 *   false positives on 4K laptops (Dell XPS, ThinkPad X1) and desktop monitors.
 *   There's no reliable, permission-free browser API to distinguish between a 4K
 *   laptop screen and a 4K TV connected via HDMI.
 * - **Window Management API** (`getScreenDetails()`) could detect external displays
 *   but requires a permission popup, has poor browser support (no Safari/Firefox),
 *   and would degrade UX for a simple scaling feature.
 * - **`screen.isExtended`** only tells us multiple screens exist, not which screen
 *   the fullscreen window is on.
 *
 * This trade-off prioritizes avoiding false positives (unwanted scaling on laptops, desktops, etc.)
 * over catching all TV scenarios.
 *
 * @param containerRef - Ref to the fullscreen container element
 * @param scale - The scale factor to apply (default: 2)
 * @returns Object containing isFullscreen, shouldScale, and the Tailwind scale class
 */
const useFullscreenScale = (containerRef: RefObject<HTMLElement>, scale: number = 2): FullscreenScaleResult => {
  const isFullscreen = useIsFullscreen(containerRef);

  if (typeof window === 'undefined') {
    return { isFullscreen: false, shouldScale: false, scaleClassName: undefined };
  }

  const { userAgent } = navigator;
  const maxViewportPx = Math.max(window.innerWidth, window.innerHeight);

  const isMobileUA = MOBILE_UA_REGEX.test(userAgent);
  const isTvUA = TV_UA_REGEX.test(userAgent);

  // Scale if in fullscreen AND we have a clear signal this is a TV context:
  // - Mobile device with large viewport (DeX, desktop mode, or casting)
  // - Smart TV or console browser (detected via UA)
  //
  // NOTE: We intentionally do NOT scale based on physical pixels or viewport size
  // alone, as this would cause false positives on 4K laptops and desktop monitors.
  const shouldScale = isFullscreen && ((isMobileUA && maxViewportPx >= TV_VIEWPORT_THRESHOLD) || isTvUA);

  let scaleClassName: string | undefined;
  if (shouldScale) {
    switch (scale) {
      case 1.5:
        scaleClassName = 'scale-150';
        break;
      case 2:
        scaleClassName = 'scale-[2]';
        break;
      default:
        scaleClassName = undefined;
    }
  }

  return { isFullscreen, shouldScale, scaleClassName };
};

export default useFullscreenScale;
